import {DataEditorProps, DataEditorRef, Item, Item as GridItem} from "@glideapps/glide-data-grid";
import {CargoSubtype, cargoSubtypes as cargoSubtypeValues, ContainerStatusEntity, TransportCategory} from "@ots-frontend/ots-data";
import React, {forwardRef, useCallback, useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {Group, useMantineTheme} from "@mantine/core";
import {Theme} from "@glideapps/glide-data-grid/dist/ts/common/styles";
import {DataGridColumnPicker} from "../DataGrid/DataGridColumnPicker";
import {useExtraCells} from "@glideapps/glide-data-grid-cells";
import {ContainerAction} from "../../menu/ContainerActionMenu/ContainerActionMenu";
import {useNavigate} from "react-router-dom";
import {ThemedDataGrid} from "../DataGrid/ThemedDataGrid";
import {ContainerStatusDataGridColOptions, useContainerStatusDataGrid} from "./useContainerStatusDataGrid";
import {containerStatusColumnId} from "./useContainerStatusDataGridCols";
// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared decision-flow dialog.
import {
  buildContainerToolGroups,
  ContainerLoadCalculationDialog,
  type ContainerToolId,
  type ContainerStatusDisplayOptions,
  type DecisionGraphModel,
  isPollStale,
  OtsToolsMenu,
} from "@ots-frontend/ots-ui-tw";
import {useOtsColorScheme} from "../DataGrid/dataGridTheme";
import {EntityDataGridProps} from "../EntityDataGrid/EntityDataGrid";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {EntityCommentListModal} from "../../comment/EntityCommentList/EntityCommentList";
import {Highlight} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-render";
import {
  ContainerStatusIcon,
  type ContainerStatusIconName,
  type ContainerStatusIconSet,
  type ContainerStatusIconVariant,
} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";
import {ObjectTagManagerModal} from "../../modal/ObjectTagManagerModal/ObjectTagManagerModal";
import {useDisclosure} from "@mantine/hooks";
import {BulkObjectTagManagerModal} from "../../modal/ObjectTagManagerModal/BulkObjectTagManagerModal";

export type ContainerStatusDataGridProps = EntityDataGridProps<ContainerStatusEntity> & {
  regionId?: string
  cargoSubtypes?: Set<CargoSubtype>,
  actions?: ContainerAction[],
  onActionClick?: (data: ContainerStatusEntity[], action: ContainerAction) => void;
  colOptions?: ContainerStatusDataGridColOptions
  /** Saved column layout key; pages with their own layout (e.g. shut-in) pass their own. */
  columnStorageKey?: string
  /** Where the column picker renders; undefined renders it above the grid, null hides it. */
  controlsTarget?: HTMLElement | null
  /** Row marks such as stale polls; none when missing. */
  display?: ContainerStatusDisplayOptions
  /** The page's cargo, for the stale poll pipeline skip. */
  transportCategory?: TransportCategory
  /** Opens a container's details panel, from Quick View in the right-click menu. */
  onQuickView?: (status: ContainerStatusEntity) => void
}

type ContainerToolsMenu = {
  point: {x: number, y: number}
  targets: ContainerStatusEntity[]
}

// The menu's flag toggles show the very icon the status column draws for that flag: same set, variant and scheme.
const getToolIcons = (
  colorScheme: "light" | "dark",
  variant: ContainerStatusIconVariant,
  iconSet: ContainerStatusIconSet | undefined,
): Partial<Record<ContainerToolId, React.ReactNode>> => {
  const statusIcon = (name: ContainerStatusIconName) =>
    <ContainerStatusIcon name={name} variant={variant} iconSet={iconSet} colorScheme={colorScheme} width={16} height={16}/>
  return {
    TOGGLE_DO_NOT_DISPATCH: statusIcon("do-not-dispatch"),
    TOGGLE_DNU: statusIcon("do-not-use"),
    TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME: statusIcon("ignore-max-non-primary-volume"),
    TOGGLE_LACT_METER_DNU: statusIcon("lact-meter-disabled"),
    TOGGLE_REJECTION: statusIcon("rejection-restriction"),
    TOGGLE_SITE_TRANSPORT_VIA_PIPELINE: statusIcon("water-pipeline"),
    TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE: statusIcon("oil-pipeline"),
  }
}

// Dark rows name their text: ThemedDataGrid gives any row with its own background but no text the dark "on light" text.
const HOVER_ROW: Record<"light" | "dark", Partial<Theme>> = {
  light: {bgCell: "#f0f0f0"},
  dark: {bgCell: "#22324D", textDark: "#F3F4F6", textMedium: "#D1D5DB", textLight: "#9CA3AF"},
}

const STALE_ROW: Record<"light" | "dark", Partial<Theme>> = {
  light: {bgCell: "#FEF3C7"},
  dark: {bgCell: "#3A2B10", textDark: "#F3F4F6", textMedium: "#D1D5DB", textLight: "#9CA3AF"},
}

/** The clock stale polls are judged by; ticks each minute so rows go stale without a refetch. */
const useMinuteClock = (enabled: boolean) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!enabled) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [enabled])
  return now
}

export const ContainerStatusDataGrid = forwardRef<DataEditorRef, ContainerStatusDataGridProps>((props, ref) => {
  const {
    data = [],
    gridSelection,
    cargoSubtypes = new Set(cargoSubtypeValues),
    onActionClick,
    actions,
    onCellClicked,
    colOptions,
    colWidths,
    // widths and sort are saved with the column layout now; the legacy callbacks only seed it
    onColumnResize: _onColumnResize,
    sort,
    onSort: _onSort,
    columnStorageKey,
    controlsTarget,
    display,
    transportCategory,
    onQuickView,
    height = "100%",
    ...rest
  } = props;

  const inheritedScheme = useOtsColorScheme();
  const appTheme = useMantineTheme();
  const colorScheme = inheritedScheme ?? appTheme.colorScheme;

  const [selectedContainers, setSelectedContainers] = useState<ContainerStatusEntity[]>([]);
  const [audit, setAudit] = useState<ContainerStatusEntity | undefined>(undefined);
  const [toolsMenu, setToolsMenu] = useState<ContainerToolsMenu>();
  const {hasAccess} = useAuth();
  const navigate = useNavigate();

  const calcsDisclosure = useDisclosure(false);
  const calcsOpened = calcsDisclosure[0]
  const calcsHandlers = calcsDisclosure[1]

  const commentsDisclosure = useDisclosure(false);
  const commentsOpened = commentsDisclosure[0]
  const commentsHandlers = commentsDisclosure[1]

  const tagsDisclosure = useDisclosure(false);
  const tagsOpened = tagsDisclosure[0]
  const tagsHandlers = tagsDisclosure[1]

  const cellProps = useExtraCells()

  const now = useMinuteClock(Boolean(display?.markStalePolls))
  const isStalePoll = useCallback((status: ContainerStatusEntity | undefined) =>
    display ? isPollStale(status, display, {now, category: transportCategory}) : false, [display, now, transportCategory])

  const dataGridArgs = useContainerStatusDataGrid({
    colOptions: colOptions,
    colWidths: colWidths,
    sort: sort,
    cargoSubtypes: cargoSubtypes,
    columnStorageKey: columnStorageKey,
    colorScheme: colorScheme,
    data: data,
    isStalePoll: isStalePoll,
  });

  const {
    hoverRow,
    getOriginalIndex,
    columnLayout,
    ...gridArgs
  } = dataGridArgs;

  const columnIndex = useCallback((id: string) =>
    dataGridArgs.columns.findIndex((column) => column.id === id), [dataGridArgs.columns]);

  const onCellContextMenu = useCallback((cell: GridItem, event: CellClickedEventArgs) => {
    event.preventDefault();

    const rootRow = getOriginalIndex(cell[1]);
    const selectedRows = gridSelection?.rows.toArray() ?? [];
    // a right-click on a selected row acts on the whole selection, as before
    const targets = selectedRows.length > 0
      ? selectedRows.map((row) => data[getOriginalIndex(row)])
      : [data[rootRow]];

    setToolsMenu({
      point: {x: event.bounds.x + event.localEventX, y: event.bounds.y + event.localEventY},
      targets: targets.filter(Boolean),
    });
  }, [data, getOriginalIndex, gridSelection]);

  const toolGroups = useMemo(() => toolsMenu ? buildContainerToolGroups(toolsMenu.targets, {
    actions: actions ?? [],
    permissions: {
      createDispatch: hasAccess("dispatch_create"),
      editDispatchRequest: hasAccess("dispatchrequest_edit"),
    },
    quickView: !!onQuickView,
    icons: getToolIcons(colorScheme === "dark" ? "dark" : "light", colOptions?.statusIconVariant ?? "filled", colOptions?.statusIconSet),
  }) : [], [toolsMenu, actions, hasAccess, onQuickView, colorScheme, colOptions?.statusIconVariant, colOptions?.statusIconSet]);

  // The menu floats over the canvas, which still sees the pointer; hold the right-clicked row's hover until it closes.
  const onItemHovered = gridArgs.onItemHovered;
  const onGridItemHovered = useCallback<NonNullable<typeof onItemHovered>>((args) => {
    if (!toolsMenu) onItemHovered?.(args);
  }, [toolsMenu, onItemHovered]);

  const onShowCalculationStepsClick = useCallback((data: ContainerStatusEntity) => {
    if (data.metadata?.measurements?.included === false) {
      return
    }
    setAudit(data)
    calcsHandlers.open()
  }, [calcsHandlers])

  const onToolAction = useCallback((id: string) => {
    const targets = toolsMenu?.targets ?? [];
    setToolsMenu(undefined);
    const [first] = targets;
    if (id === "VIEW") {
      if (first) navigate(`/containers/profile?id=${first.containerId}`);
    } else if (id === "QUICK_VIEW") {
      if (first) onQuickView?.(first);
    } else if (id === "CALCULATION_STEPS") {
      if (first) onShowCalculationStepsClick(first);
    } else if (id === "MANAGE_TAGS") {
      setSelectedContainers(targets);
      tagsHandlers.open();
    } else {
      onActionClick?.(targets, id as ContainerAction);
    }
  }, [toolsMenu, navigate, onQuickView, onShowCalculationStepsClick, tagsHandlers, onActionClick])

  const getRowThemeOverride = useCallback((row: number): Partial<Theme> | undefined => {
    if (hoverRow === row) {
      return HOVER_ROW[colorScheme === "dark" ? "dark" : "light"];
    }

    if (isStalePoll(data[getOriginalIndex(row)])) {
      return STALE_ROW[colorScheme === "dark" ? "dark" : "light"];
    }

    return undefined;
  }, [hoverRow, isStalePoll, data, getOriginalIndex, colorScheme]);

  const highlights = useMemo<DataEditorProps["highlightRegions"]>(() => {
    if (gridSelection?.current === undefined) {
      return undefined;
    }
    const [_col, row] = gridSelection.current.cell;
    const containerStatus = data[getOriginalIndex(row)];
    const containerColumn = columnIndex(containerStatusColumnId.container);
    if (containerStatus && containerStatus.containerClusterId && containerColumn >= 0) {

      const highlights: Highlight[] = []

      data.forEach((value, index) => {
        if (data[getOriginalIndex(index)].containerClusterId === containerStatus.containerClusterId) {
          highlights.push({
            color: "#22A7F030",
            range: {
              x: containerColumn,
              y: index,
              width: 1,
              height: 1
            },
            style: "dashed"
          });
        }
      })

      return highlights
    }

    return undefined
  }, [gridSelection, data, getOriginalIndex, columnIndex]);

  const _onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    if (dataGridArgs.columns[cell[0]]?.id === containerStatusColumnId.status) {
      const containerStatus = data[getOriginalIndex(cell[1])];
      if (containerStatus) {
        setSelectedContainers([containerStatus])
      }
    }

    if (onCellClicked) {
      onCellClicked(cell, event)
    }
  }, [onCellClicked, data, getOriginalIndex, dataGridArgs.columns])

  const onCloseCalcs = useCallback(() => {
    calcsHandlers.close()
    setAudit(undefined)
  }, [calcsHandlers])

  const onCloseComments = useCallback(() => {
    commentsHandlers.close()
    setSelectedContainers([])
  }, [commentsHandlers])

  const onCloseTags = useCallback(() => {
    tagsHandlers.close()
    setSelectedContainers([])
  }, [tagsHandlers])

  const controls = (
    <Group position="right" mb={controlsTarget === undefined ? "xs" : 0}>
      <DataGridColumnPicker {...columnLayout}/>
    </Group>
  );

  return (
    <>
      <div style={{display: "flex", flexDirection: "column", height, minHeight: 0}}>
      {controlsTarget === undefined ? controls : controlsTarget ? createPortal(controls, controlsTarget) : null}
      <div style={{flex: 1, minHeight: 0}}>
      <ThemedDataGrid
        {...rest}
        {...gridArgs}
        {...cellProps}
        colorScheme={colorScheme}
        ref={ref}
        height="100%"
        width="100%"
        smoothScrollX={true}
        smoothScrollY={true}
        rowMarkers="both"
        getCellsForSelection={true}
        theme={{
          cellVerticalPadding: 8
        }}
        onCellClicked={onCellClicked ? _onCellClicked : undefined}
        getRowThemeOverride={getRowThemeOverride}
        onCellContextMenu={onCellContextMenu}
        onItemHovered={onGridItemHovered}
        gridSelection={gridSelection}
        highlightRegions={highlights}
        rowSelect="multi"
        rowSelectionMode="multi"/>
      </div>
      </div>
      {toolsMenu &&
        <OtsToolsMenu
          compact
          layout="list"
          label={toolsMenu.targets.length > 1 ? `${toolsMenu.targets.length} containers` : toolsMenu.targets[0]?.containerName ?? "Container"}
          anchor={toolsMenu.point}
          groups={toolGroups}
          onAction={onToolAction}
          onClose={() => setToolsMenu(undefined)}/>
      }
      <ContainerLoadCalculationDialog
        open={calcsOpened && !!audit}
        onClose={onCloseCalcs}
        colorScheme={colorScheme}
        containerName={audit?.containerName}
        graph={audit?.meterFill?.availableLoadDecisionGraph as DecisionGraphModel | undefined}
      />
      {selectedContainers.length > 0 &&
        <EntityCommentListModal
          opened={commentsOpened}
          onClose={onCloseComments}
          withinPortal={true}
          centered={true}
          mih={400}
          listProps={{
            objectId: selectedContainers[0].containerId,
          }}
        />
      }
      {selectedContainers.length > 0 &&
        <>
          {selectedContainers.length === 1 ? (
            <ObjectTagManagerModal
              opened={tagsOpened}
              onClose={onCloseTags}
              withinPortal={true}
              centered={true}
              mih={400}
              entity={{
                objectId: selectedContainers[0].containerId,
                objectType: "CONTAINER",
                objectName: selectedContainers[0].containerName,
              }}
            />
          ) : (
            <BulkObjectTagManagerModal
              opened={tagsOpened}
              onClose={onCloseTags}
              withinPortal={true}
              centered={true}
              mih={400}
              entities={selectedContainers.map((container) => ({
                objectId: container.containerId,
                objectType: "CONTAINER",
                objectName: container.containerName,
              }))}
            />
          )
          }
        </>
      }
    </>
  );
});
